import { computed, ref, shallowRef, toRef, watch } from 'vue'

import { useStore } from '@/store'
import { buildPatchedAttributes } from '@/utils/modelMetaPatches'

// Fields excluded from standard field listing because they have
// dedicated rendering (injected lists, file trees, custom panels).
// The server correctly marks them visible — they ARE on the page,
// just not through the standard field component iteration.
const DEDICATED_PANEL_FIELDS = {
  projects: ['experiments', 'studies'],
  datasetcollections: ['datasets'],
  datasets: ['datafiles'],
  workflows: ['workflowversions'],
  workflowversions: ['workflowfiles'],
  workflowruns: ['workflowrundatasets', 'run_command', 'run_environment'],
  instrumentruns: ['assays'],
}

// This is not in charge of loading the model meta (it's in the store)
// from a signaure (app, model, type) it returns meta info

export function useRecordMeta({
  appName,
  modelName,
  modelType,
  skipFieldNames = [],
}) {
  // Normalize all params to refs so callers can pass plain values or refs
  appName = toRef(appName)
  modelName = toRef(modelName)
  modelType = toRef(modelType)
  skipFieldNames = ref(skipFieldNames)
  const store = useStore()
  const isMetaReady = computed(() => store.getters['meta/isMetaReady'])
  const annotationTypes = computed(() => {
    // that this composable returns a map of annotation types is not useful
    // now (vs fetching this info from store) but could become valuable when
    // available annotation types are restricted to specific item types
    const amm = store.getters['meta/annotationMetaModels']
    return amm.reduce((acc, obj) => ({ ...acc, [obj.name]: obj }), {})
  })
  const meta = computed(() =>
    store.getters['meta/getMeta'](
      appName.value,
      modelName.value,
      modelType.value,
    ),
  )
  const model = computed(() => meta.value.model)

  const isAnnotatable = computed(() => model.value.annotatable === true)
  const isAttachmentable = computed(() => model.value.attachmentable === true)
  const isOrderable = computed(() => model.value.orderable === true)
  const isNoteable = computed(() => model.value.noteable === true)
  const isShippable = computed(() => model.value.shippable === true)
  const view = computed(() => meta.value.view)
  const viewAttributes = computed(() => {
    const viewAttrs = view.value?.detail?.attributes || []
    return viewAttrs.reduce(
      (viewAttrs, attr) => ({ ...viewAttrs, [attr.name]: attr }),
      {},
    )
  })
  // ── Reactive field meta patches ──────────────────────────────
  // Composables and views can register computed patches that overlay
  // on top of server-provided model attributes at runtime.
  // Each patch is a computed ref returning { fieldName: { ...overrides } }.
  const fieldMetaPatches = shallowRef([])

  function addFieldMetaPatch(computedPatch) {
    fieldMetaPatches.value = [...fieldMetaPatches.value, computedPatch]
  }

  // Static server-patched attributes (patchModelMeta workarounds)
  const rawModelAttributes = computed(() =>
    buildPatchedAttributes(
      model.value?.attributes || [],
      viewAttributes.value,
      modelName.value,
      modelType.value,
    ),
  )

  // Final modelAttributes with reactive patches applied on top
  const modelAttributes = computed(() => {
    const base = rawModelAttributes.value
    const patches = fieldMetaPatches.value
    if (!patches.length) return base

    const result = {}
    for (const [name, field] of Object.entries(base)) {
      let patched = field
      for (const patch of patches) {
        const override = patch.value?.[name]
        if (override) patched = { ...patched, ...override }
      }
      result[name] = patched
    }
    return result
  })

  const defaultRecord = ref({})

  watch(
    modelAttributes,
    (attrs) => {
      Object.entries(attrs).forEach(([fieldName, field]) => {
        defaultRecord.value[fieldName] = { value: field.default }
      })
    },
    { immediate: true, deep: true },
  )

  const orderedFields = computed(() => {
    const attrs = modelAttributes.value
    // Build a position map from view detail attributes (server-defined order).
    // Fields absent from view get Infinity so they sort to the end.
    const viewOrder = view.value?.detail?.attributes || []
    const viewPos = {}
    for (const va of viewOrder) viewPos[va.name] = va.order
    // Sort all model attributes: view-positioned first (by server order),
    // then any extras with a synthetic order value injected via patchModelMeta,
    // then truly unpositioned fields last (stable by Object.values insertion order).
    return Object.values(attrs)
      .slice()
      .sort((a, b) => {
        const posA = viewPos[a.name] ?? a.order ?? Infinity
        const posB = viewPos[b.name] ?? b.order ?? Infinity
        return posA - posB
      })
  })

  // All fields the server marks as visible on this page.
  // visible: true comes from view.detail.attributes (patched for id/permissions
  // via patchModelMeta TODO-SERVER-010/011).
  const visibleFields = computed(() =>
    orderedFields.value.filter(({ visible }) => visible === true),
  )

  // Fields for standard field component iteration. Excludes:
  // - DEDICATED_PANEL_FIELDS: fields with dedicated rendering (injected lists,
  //   file trees, custom panels) — centralised, not per-view
  // - skipFieldNames: per-view exclusions (e.g. "name" rendered in header)
  const listedFields = computed(() => {
    const dedicated = DEDICATED_PANEL_FIELDS[modelName.value] || []
    const excludeSet = new Set([...dedicated, ...skipFieldNames.value])
    if (!excludeSet.size) return visibleFields.value
    return visibleFields.value.filter((f) => !excludeSet.has(f.name))
  })

  // CPTs sorted by FieldOrder.order — same server-defined position that
  // orderedFields uses for regular fields. Server-emitted order in
  // model.attributes can put CPTs at the tail regardless of their configured
  // FieldOrder position; sourcing from orderedFields sorts them correctly.
  const customProperties = computed(() =>
    orderedFields.value.filter((attr) => attr.category !== 'property'),
  )

  return {
    meta,
    isMetaReady,
    modelAttributes,
    viewAttributes,
    annotationTypes,
    defaultRecord,
    // fields
    visibleFields,
    listedFields,
    orderedFields,
    customProperties,
    // commons
    isAnnotatable,
    isAttachmentable,
    isOrderable,
    isNoteable,
    isShippable,
    addFieldMetaPatch,
  }
}
